import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Row,
  Col,
  message,
} from "antd";
import Swal from "sweetalert2";
import { config } from "../../config/config";
import CryptoJS from "crypto-js";
import axios from "axios";
import LogoImg from "../../assets/images/Logo.png";
import BVNLicenceHistory from "./History/BVNLicenceHistory.jsx";
import { motion } from "framer-motion";
import {
  UserOutlined,
  SafetyCertificateOutlined,
  EnvironmentOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";

function BVNLicence() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [states, setStates] = useState([]);
  const [lgas, setLgas] = useState([]);
  const [banks, setBanks] = useState([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const [banksLoading, setBanksLoading] = useState(false);
  const [isBankOpen, setIsBankOpen] = useState(false);
  const [isStateOpen, setIsStateOpen] = useState(false);
  const [isLgaOpen, setIsLgaOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [progress, setProgress] = useState(0);
  const [amount, setAmount] = useState(100); // Default amount
  const [priceLoading, setPriceLoading] = useState(true);

  const calculateProgress = (allValues) => {
    const requiredFields = [
      "title", "email", "firstName", "lastName", "phone", 
      "accountNumber", "bankName", "address", "dob", 
      "stateOfResidence", "lga", "city", "wardName", "nin", "bvn"
    ];
    let filledCount = 0;
    requiredFields.forEach((field) => {
      if (allValues[field] !== undefined && allValues[field] !== null && allValues[field] !== "") {
        filledCount++;
      }
    });
    return Math.round((filledCount / requiredFields.length) * 100);
  };

  const handleValuesChange = (_, allValues) => {
    setProgress(calculateProgress(allValues));
  };

  useEffect(() => {
    setProgress(calculateProgress(form.getFieldsValue()));
  }, [form, refreshKey]);

  // Fetch states effect
  useEffect(() => {
    fetch("https://nga-states-lga.onrender.com/fetch")
      .then((res) => res.json())
      .then((data) => setStates(data))
      .catch(() => setStates([]));
  }, []);

  // Fetch LGAs effect
  const handleStateChange = (value) => {
    setLocationLoading(true);
    fetch(`https://nga-states-lga.onrender.com/?state=${value}`)
      .then((res) => res.json())
      .then((data) => setLgas(data))
      .catch(() => setLgas([]))
      .finally(() => setLocationLoading(false));
  };

  const SECRET_KEY = import.meta.env.VITE_APP_SECRET_KEY;

  function decryptData(ciphertext) {
    if (!ciphertext) return null;
    try {
      const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_KEY);
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);
      return JSON.parse(decrypted);
    } catch {
      return null;
    }
  }

  // Get userId from encrypted localStorage
  let userId = null;
  try {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      const userObj = decryptData(userStr);
      userId = userObj?._id || userObj?.id;
    }
  } catch (error) {}

  // History is handled by the imported BVNLicenceHistory component

  const onFinish = async (values) => {
    // Show confirmation dialog first
    const result = await Swal.fire({
      title: "Confirm BVN User Registration",
      html: `
        <p class="mb-4 text-gray-500">Are you sure you want to register this BVN user?</p>
        <div class="bg-slate-50 border border-slate-100 rounded-2xl p-4 space-y-2 text-left mb-4">
          <p class="text-sm text-gray-600"><strong>Name:</strong> ${values.firstName} ${values.lastName}</p>
          <p class="text-sm text-gray-600"><strong>BVN:</strong> ${values.bvn}</p>
        </div>
        <div class="bg-sky-50 border-2 border-sky-200 rounded-2xl py-3 px-6 inline-block shadow-sm">
          <span class="text-xs font-bold text-sky-500 uppercase tracking-wider block mb-0.5">Verification Fee</span>
          <span class="text-3xl font-black text-sky-600">₦${amount.toLocaleString()}</span>
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#f59e0b",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, register",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) {
      return;
    }

    setLoading(true);

    try {
      const payload = {
        userId: userId,
        title: values.title,
        bankName: values.bankName,
        bvn: values.bvn,
        accountNumber: values.accountNumber,
        firstName: values.firstName,
        lastName: values.lastName,
        middleName: values.middleName || "",
        email: values.email,
        phone: values.phone,
        address: values.address,
        dob: values.dob.format("YYYY-MM-DD"),
        stateOfResidence: values.stateOfResidence,
        lga: values.lga,
        city: values.city,
        wardName: values.wardName,
        nin: values.nin,
        amount: amount,
      };

      const response = await axios.post(
        `${config.apiBaseUrl}${config.endpoints.bvnLicenceRegistration}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );
        await Swal.fire({
          icon: "success",
          title: "Registration Successful!",
          text: "Your BVN User registration has been submitted successfully.",
          confirmButtonColor: "#f59e0b",
        });

        form.resetFields();
        setRefreshKey((prev) => prev + 1);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Registration Failed",
        text: error.response?.data?.message || "Failed to submit registration. Please try again.",
        confirmButtonColor: "#f59e0b",
      });
    } finally {
      setLoading(false);
    }
  };

  // Add this effect to fetch banks
  useEffect(() => {
    setBanksLoading(true);
    fetch("https://api.paystack.co/bank", {
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response) => {
        if (response.status && response.data) {
          const banksList = response.data.map((bank) => ({
            id: bank.id,
            code: bank.code,
            name: bank.name,
          }));
          setBanks(banksList);
        }
      })
      .catch((error) => {
        message.error("Failed to load banks");
      })
      .finally(() => setBanksLoading(false));
  }, []);

  // Fetch BVN User pricing
  useEffect(() => {
    const fetchPrices = async () => {
      setPriceLoading(true);
      try {
        const response = await axios.get(
          `${config.apiBaseUrl}${config.endpoints.currentapipricing}`,
          { withCredentials: true }
        );
        
        // Find BVN User pricing
        const bvnUserPricingData = Array.isArray(response.data)
          ? response.data.find((item) => item.key === "bvn_user")
          : response.data;

        const bvnUserPricing =
          bvnUserPricingData?.key === "bvn_user" ? bvnUserPricingData : null;

        if (bvnUserPricing && bvnUserPricing.prices) {
          setAmount(bvnUserPricing.prices.agent);
        }
      } catch (error) {
        message.error("Failed to fetch current price");
      } finally {
        setPriceLoading(false);
      }
    };

    fetchPrices();
  }, []);

  // effect to handle body scroll
  useEffect(() => {
    if (isBankOpen || isStateOpen || isLgaOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isBankOpen, isStateOpen, isLgaOpen]);

  return (
    <div className="w-full relative bg-slate-50/30 px-4 py-4 rounded-3xl overflow-hidden">
      <style>{`
        .bvn-form .ant-input,
        .bvn-form .ant-select-selector,
        .bvn-form .ant-picker {
          border-radius: 8px !important;
          border-color: #e2e8f0 !important;
          height: 36px !important;
          font-size: 0.875rem !important;
        }
        .bvn-form .ant-select-selector {
          align-items: center !important;
        }
        .bvn-form .ant-form-item-label > label {
          font-weight: 600 !important;
          color: #475569 !important;
          font-size: 0.78rem !important;
          height: auto !important;
        }
        .bvn-form .ant-form-item {
          margin-bottom: 10px !important;
        }
        .bvn-form .ant-picker {
          width: 100% !important;
        }
      `}</style>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-3xl mx-auto rounded-2xl bg-white shadow-md border border-gray-100 overflow-hidden mb-6"
      >
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-5 py-3 border-b border-gray-100 bg-gradient-to-r from-sky-50 to-indigo-50">
          <div>
            <h2 className="text-base sm:text-lg font-extrabold text-slate-800 leading-tight">BVN User Registration</h2>
            <p className="text-slate-500 text-xs mt-0.5">Fill in all details below to register</p>
          </div>
          <div className="self-start sm:self-auto bg-white px-3 py-1 rounded-xl border-2 border-sky-200 text-sky-600 font-black text-sm shadow-sm whitespace-nowrap">
            {priceLoading ? "Loading..." : `₦${amount.toLocaleString()}`}
          </div>
        </div>

        {/* Form */}
        <div className="px-5 py-4">
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            onValuesChange={handleValuesChange}
            requiredMark={false}
            className="bvn-form"
          >
            {/* Row 1: Title | Email */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
              <Form.Item name="title" label="Title" rules={[{ required: true, message: "Required" }]}>
                <Select placeholder="Select Title">
                  {["Mr","Mrs","Miss","Ms","Dr","Prof","Chief"].map(t => (
                    <Select.Option key={t} value={t}>{t}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="email" label="Email *" rules={[{ required: true, message: "Required" }, { type: "email", message: "Invalid email" }]}>
                <Input placeholder="Email Address" />
              </Form.Item>
            </div>

            {/* Row 2: First Name | Last Name */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
              <Form.Item name="firstName" label="First Name" rules={[{ required: true, message: "Required" }]}>
                <Input placeholder="First Name" />
              </Form.Item>
              <Form.Item name="lastName" label="Last Name" rules={[{ required: true, message: "Required" }]}>
                <Input placeholder="Last Name" />
              </Form.Item>
            </div>

            {/* Row 3: Middle Name | Phone */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
              <Form.Item name="middleName" label="Middle Name">
                <Input placeholder="Middle Name" />
              </Form.Item>
              <Form.Item name="phone" label="Phone" rules={[{ required: true, message: "Required" }, { pattern: /^\d{11}$/, message: "Must be 11 digits" }]}>
                <Input placeholder="Phone Number" maxLength={11} />
              </Form.Item>
            </div>

            {/* Row 4: Account Number | Bank Name */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
              <Form.Item name="accountNumber" label="Acct No" rules={[{ required: true, message: "Required" }, { pattern: /^\d{10}$/, message: "Must be 10 digits" }]}>
                <Input placeholder="Account Number" maxLength={10} />
              </Form.Item>
              <Form.Item name="bankName" label="Bank Name" rules={[{ required: true, message: "Required" }]}>
                <Select
                  placeholder="Select Bank"
                  showSearch
                  onOpenChange={(open) => setIsBankOpen(open)}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {banks.map((bank, index) => (
                    <Select.Option key={index} value={bank.name}>{bank.name}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            {/* Row 5: Street Address | DOB */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
              <Form.Item name="address" label="Street Address" rules={[{ required: true, message: "Required" }]}>
                <Input placeholder="House No & Street Name" />
              </Form.Item>
              <Form.Item name="dob" label="Date of Birth" rules={[{ required: true, message: "Required" }]}>
                <DatePicker className="w-full" placeholder="Select DOB" />
              </Form.Item>
            </div>

            {/* Row 6: LGA | State */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
              <Form.Item name="lga" label="LGA" rules={[{ required: true, message: "Required" }]}>
                <Select
                  loading={locationLoading}
                  placeholder="Select LGA"
                  disabled={!form.getFieldValue("stateOfResidence")}
                  onOpenChange={(open) => setIsLgaOpen(open)}
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {lgas.map((lga, idx) => (
                    <Select.Option key={idx} value={lga}>{lga}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="stateOfResidence" label="State" rules={[{ required: true, message: "Required" }]}>
                <Select
                  onChange={handleStateChange}
                  loading={locationLoading}
                  placeholder="Select State"
                  onOpenChange={(open) => setIsStateOpen(open)}
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {states.map((state, idx) => (
                    <Select.Option key={idx} value={state}>{state}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            {/* Row 7: City | Ward Name */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
              <Form.Item name="city" label="City" rules={[{ required: true, message: "Required" }]}>
                <Input placeholder="City" />
              </Form.Item>
              <Form.Item name="wardName" label="Ward Name" rules={[{ required: true, message: "Required" }]}>
                <Input placeholder="Ward Name" />
              </Form.Item>
            </div>

            {/* Row 8: NIN | BVN */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
              <Form.Item name="nin" label="NIN No" rules={[{ required: true, message: "Required" }, { pattern: /^\d{11}$/, message: "Must be 11 digits" }]}>
                <Input placeholder="Enter NIN" maxLength={11} />
              </Form.Item>
              <Form.Item name="bvn" label="BVN No" rules={[{ required: true, message: "Required" }, { pattern: /^\d{11}$/, message: "Must be 11 digits" }]}>
                <Input placeholder="Enter BVN" maxLength={11} />
              </Form.Item>
            </div>


            {/* Submit */}
            <div className="pt-2 border-t border-gray-100 mt-2">
              <Button
                type="primary"
                htmlType="submit"
                size="middle"
                loading={loading}
                className="w-full rounded-xl bg-gradient-to-r from-sky-500 to-indigo-600 border-0 font-semibold h-10"
              >
                {loading ? "Registering..." : `Register & Submit (₦${amount.toLocaleString()})`}
              </Button>
            </div>
          </Form>
        </div>
      </motion.div>

      {/* History */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
        className="w-full max-w-3xl mx-auto"
      >
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <BVNLicenceHistory key={refreshKey} />
        </div>
      </motion.div>
    </div>
  );
}

export default BVNLicence;

